import { useState } from "react";
import { Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Periodo, StatusInstituicao, StatusUsuario } from "./api";
import { PRESETS_DIAS, STATUS_INFO, TOOLTIP_ACESSO, rotuloStatus } from "./formato";

export function StatusBadge({
  status,
  tipo,
  className,
}: {
  status: StatusInstituicao | StatusUsuario;
  tipo: "instituicao" | "usuario";
  className?: string;
}) {
  const { cor } = STATUS_INFO[status];
  return (
    <span
      className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-border px-2 py-0.5 text-xs font-medium", className)}
    >
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: cor }} aria-hidden />
      {rotuloStatus(status, tipo)}
    </span>
  );
}

/** "O que conta como acesso" — a limitação do login_log, sempre à vista. */
export function InfoAcesso() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-muted-foreground hover:text-foreground" aria-label="O que conta como acesso">
          <Info className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{TOOLTIP_ACESSO}</TooltipContent>
    </Tooltip>
  );
}

export function PeriodoSelect({ periodo, dias, onChange }: { periodo: Periodo; dias?: number; onChange: (p: { dias: number } | Periodo) => void }) {
  const [personalizado, setPersonalizado] = useState(!dias);
  const [rascunho, setRascunho] = useState<Periodo>(periodo);

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Select
        value={personalizado ? "personalizado" : String(dias)}
        onValueChange={(v) => {
          if (v === "personalizado") {
            setRascunho(periodo);
            setPersonalizado(true);
            return;
          }
          setPersonalizado(false);
          onChange({ dias: Number(v) });
        }}
      >
        <SelectTrigger className="w-44" aria-label="Período">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESETS_DIAS.map((d) => (
            <SelectItem key={d} value={String(d)}>
              Últimos {d} dias
            </SelectItem>
          ))}
          <SelectItem value="personalizado">Personalizado</SelectItem>
        </SelectContent>
      </Select>
      {personalizado && (
        <>
          <Input
            type="date"
            aria-label="Início do período"
            className="w-40"
            value={rascunho.inicio}
            onChange={(e) => setRascunho((r) => ({ ...r, inicio: e.target.value }))}
          />
          <Input
            type="date"
            aria-label="Fim do período"
            className="w-40"
            value={rascunho.fim}
            onChange={(e) => setRascunho((r) => ({ ...r, fim: e.target.value }))}
          />
          <Button
            variant="outline"
            disabled={!rascunho.inicio || !rascunho.fim || rascunho.inicio > rascunho.fim}
            onClick={() => onChange(rascunho)}
          >
            Aplicar
          </Button>
        </>
      )}
    </div>
  );
}

/** Campo do toolbar com rótulo, no padrão das telas de consulta. */
export function Campo({ id, label, className, children }: { id: string; label: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}
